import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import heroSide from '../assets/images/jetnseal-pressure-washer-uk.png';
import patioCleaningWood from '../assets/images/jetnseal-wooden-pressure-washer-uk.png';
import patioCleaningCard from '../assets/images/jetnseal-patio-cleaning-near-me.png';
import pressureWashingCard from '../assets/images/jetnseal-outdoor-cleaning-products.png';
import blockPavingCard from '../assets/images/jetnseal-pressure-washer-surface-cleaner.png';
import whyChooseUsImage from '../assets/images/jetnseal-best-pressure-washer-uk.png';

const { FiCheck, FiStar, FiMail, FiMapPin, FiClock, FiShield, FiAward, FiUsers } = FiIcons;

const Home = () => {
  const services = [
    {
      title: 'Patio & Decking Cleaning',
      description: 'Professional cleaning for patios and wooden decking to restore your outdoor space to its former glory.',
      image: patioCleaningWood
    },
    {
      title: 'Pressure Washing',
      description: 'High-pressure cleaning for driveways, paths, and all outdoor surfaces.',
      image: patioCleaningCard
    },
    {
      title: 'Block Paving Cleaning',
      description: 'Remove weeds, re-sand joints, and restore block paving to its original appearance.',
      image: blockPavingCard
    }
  ];

  const features = [
    { icon: FiShield, title: 'Fully Insured', description: 'Complete peace of mind with comprehensive insurance coverage' },
    { icon: FiAward, title: 'Expert Service', description: 'Years of experience in professional outdoor cleaning' },
    { icon: FiUsers, title: 'Local Team', description: 'Trusted local professionals serving your community' }
  ];

  const serviceAreas = ['Malton', 'Norton', 'Pickering', 'Rillington', 'Thornton-le-Dale', 'Kirkbymoorside'];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1200&h=800&fit=crop)'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                  Professional
                  <span className="block text-blue-300">Patio Cleaning</span>
                  <span className="block text-3xl lg:text-4xl">& Pressure Washing</span>
                </h1>
                <p className="text-xl text-blue-100 mt-6">
                  Expert outdoor surface cleaning services across Malton, Norton, Pickering and surrounding areas. 
                  Transform your patio, driveway, and outdoor spaces with our professional cleaning solutions.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact-us"
                  className="bg-blue-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-600 transition-colors duration-200 text-center"
                >
                  Get Free Quote
                </Link>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 pt-8">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    className="text-center"
                  >
                    <SafeIcon icon={feature.icon} className="w-8 h-8 text-blue-300 mx-auto mb-2" />
                    <h3 className="font-semibold text-sm">{feature.title}</h3>
                    <p className="text-xs text-blue-200 mt-1">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src={heroSide}
                alt="Professional pressure washing service"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Cleaning Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From patio cleaning to comprehensive pressure washing, we provide professional outdoor cleaning solutions 
              tailored to your needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <Link
                    to="/contact-us"
                    className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors mt-auto"
                  >
                    Get in touch
                    <SafeIcon icon={FiCheck} className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={whyChooseUsImage}
                alt="Before and after patio cleaning"
                className="rounded-2xl shadow-lg"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Why Choose Jet'n'Seal?
                </h2>
                <p className="text-xl text-gray-600">
                  We're your local experts in patio cleaning and pressure washing, serving the communities of 
                  Malton, Norton, Pickering and surrounding areas with professional outdoor cleaning services.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  'Professional pressure washer surface cleaner equipment',
                  'Eco-friendly outdoor cleaning products',
                  'Fully insured and experienced team',
                  'Competitive pricing with no hidden costs',
                  'Free quotes and consultations',
                  'Satisfaction guarantee on all work'
                ].map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/why-us"
                className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                Learn More About Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Areas We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Providing professional patio cleaning and pressure washing services across North Yorkshire
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {serviceAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-blue-50 text-blue-800 px-4 py-3 rounded-lg text-center font-medium hover:bg-blue-100 transition-colors"
              >
                {area}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">
              Don't see your area listed? <Link to="/contact-us" className="text-blue-600 hover:text-blue-700 font-medium">Contact us</Link> to check availability.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h2 className="text-4xl font-bold">
              Ready to Transform Your Outdoor Space?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Get your free quote today for professional patio cleaning and pressure washing services. 
              No obligation, just expert advice and competitive pricing.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact-us"
                className="bg-white text-blue-800 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-200"
              >
                Get Free Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;